import React from 'react';
import { Info, FileText, Calculator, PiggyBank, Zap, AlertTriangle, CreditCard } from 'lucide-react';

/**
 * Компонент с информацией о самозанятости и налогах в России
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о самозанятости в России: условия, налоги, советы
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" />
          Что такое самозанятость и налог на профессиональный доход (НПД)?
        </h3>
        <p>
          <strong>Самозанятость</strong> — это специальный налоговый режим (налог на профессиональный доход, НПД), который позволяет гражданам и ИП легально вести деятельность без регистрации юридического лица и платить налог по льготной ставке.
        </p>
        <p>
          НПД введен Федеральным законом от 27.11.2018 № 422-ФЗ и с 2019 года действует на всей территории России. Этот режим позволяет легально заниматься бизнесом и получать доход от подработок без рисков получить штраф за незаконную предпринимательскую деятельность.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Ключевые условия и ограничения НПД
        </h3>
        <ul>
          <li><strong>Ставки налога:</strong>
            <ul>
              <li>4% при работе с физическими лицами;</li>
              <li>6% при работе с юридическими лицами и ИП.</li>
            </ul>
          </li>
          <li><strong>Налоговый вычет:</strong> до 10 000 рублей (уменьшает налог на 1% с физлиц и 2% с юрлиц).</li>
          <li><strong>Максимальный доход:</strong> 2,4 млн рублей в год (примерно 200 000 рублей в месяц).</li>
          <li><strong>Ограничения:</strong>
            <ul>
              <li>Нельзя нанимать работников по трудовым договорам;</li>
              <li>Нельзя совмещать с другими налоговыми режимами для одного и того же вида деятельности;</li>
              <li>Нельзя заниматься перепродажей товаров, кроме товаров собственного изготовления;</li>
              <li>Нельзя добывать и продавать полезные ископаемые;</li>
              <li>Нельзя работать по договорам поручения, комиссии или агентским договорам от имени третьих лиц;</li>
              <li>Нельзя применять НПД для работы с текущим или бывшим работодателем (менее двух лет).</li>
            </ul>
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-green-500" />
          Как стать самозанятым
        </h3>
        <p>
          Регистрация самозанятости занимает всего несколько минут. Есть несколько способов:
        </p>
        <ol>
          <li><strong>Приложение «Мой налог»</strong> — официальное приложение ФНС России для самозанятых.</li>
          <li><strong>Личный кабинет самозанятого</strong> на сайте ФНС России (npd.nalog.ru).</li>
          <li><strong>Через банки-партнеры</strong> ФНС, которые интегрировали функциональность для самозанятых в свои приложения.</li>
          <li><strong>Портал Госуслуг</strong> (gosuslugi.ru) — для пользователей с подтвержденной учетной записью.</li>
        </ol>
        <p>
          Для регистрации нужны паспорт и фотография (которую можно сделать прямо в приложении).
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Преимущества самозанятости:</h4>
          <ul className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            <li>Низкие налоговые ставки (4% и 6%)</li>
            <li>Простая регистрация и отсутствие отчетности</li>
            <li>Не нужно платить обязательные страховые взносы</li>
            <li>Не нужно применять кассу (чеки формируются в приложении)</li>
            <li>Возможность легально получать оплату за услуги и товары от физлиц и компаний</li>
            <li>Подтверждение официального дохода (для банков, визовых центров и др.)</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-green-500" />
          Налоговый вычет для самозанятых
        </h3>
        <p>
          У самозанятых есть право на налоговый вычет в размере 10 000 рублей, который автоматически уменьшает сумму налога к уплате:
        </p>
        <ul>
          <li>
            <strong>При ставке 4%</strong> вычет фактически снижает ставку до 3% до тех пор, пока не будет исчерпан лимит в 10 000 рублей.
          </li>
          <li>
            <strong>При ставке 6%</strong> вычет снижает ставку до 4% до исчерпания лимита.
          </li>
        </ul>
        <p>
          Вычет начисляется автоматически, его размер можно увидеть в приложении «Мой налог». Этот бонус помогает снизить налоговую нагрузку в начале деятельности.
        </p>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          Сравнение с другими налоговыми режимами
        </h3>
        <p>
          Помимо самозанятости (НПД), предприниматели могут выбрать и другие налоговые режимы:
        </p>
        <table className="min-w-full border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Параметр</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Самозанятость (НПД)</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">УСН Доходы</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">УСН Доходы-Расходы</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            <tr>
              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">Налоговая ставка</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">4% для физлиц<br />6% для юрлиц и ИП</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">6% от доходов</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">15% от доходов минус расходы</td>
            </tr>
            <tr>
              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">Лимит дохода</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">2,4 млн руб. в год</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">219,2 млн руб. в год</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">219,2 млн руб. в год</td>
            </tr>
            <tr>
              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">Страховые взносы</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Не обязательны</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Обязательны для ИП</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Обязательны для ИП</td>
            </tr>
            <tr>
              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">Учет расходов</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Нет</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Нет</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Да</td>
            </tr>
            <tr>
              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">Отчетность</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Нет</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Годовая декларация</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Годовая декларация</td>
            </tr>
            <tr>
              <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">Наем сотрудников</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Нельзя</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Можно</td>
              <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Можно</td>
            </tr>
          </tbody>
        </table>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Важные особенности и ограничения режима НПД
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Самозанятый не может работать по трудовому договору с бывшим работодателем в течение 2 лет</li>
            <li>Доход учитывается только при поступлении денег или имущества в счет оплаты</li>
            <li>Доходы от работы в рамках НПД не учитываются при расчете НДФЛ</li>
            <li>Самозанятым не нужно платить НДС (кроме случаев импорта товаров)</li>
            <li>При превышении лимита в 2,4 млн рублей необходимо перейти на другую систему налогообложения</li>
            <li>НПД нельзя совмещать с другими налоговыми режимами для одного вида деятельности</li>
          </ul>
        </div>

        <h3>Учет доходов и формирование чеков</h3>
        <p>
          В режиме НПД предприниматель обязан формировать чеки по каждому поступлению средств:
        </p>
        <ol>
          <li>При расчетах наличными или по безналичному расчету чек формируется в приложении «Мой налог»</li>
          <li>В чеке нужно указать сумму расчета и вид деятельности</li>
          <li>Сформированный чек можно передать покупателю или заказчику (на бумаге или в электронном виде)</li>
          <li>На основании чеков автоматически формируется налоговая база</li>
          <li>ФНС ежемесячно рассчитывает налог и уведомляет самозанятого о необходимости оплаты</li>
        </ol>

        <h3>Добровольное пенсионное страхование</h3>
        <p>
          Самозанятые не делают обязательных пенсионных отчислений, что влияет на будущую пенсию. Однако есть возможность делать добровольные взносы:
        </p>
        <ul>
          <li><strong>Через приложение «Мой налог»</strong> — можно подключить автоплатеж в ПФР</li>
          <li><strong>Через портал Госуслуг</strong> — можно оформить добровольное пенсионное страхование</li>
          <li><strong>В отделении ПФР</strong> — необходимо заключить договор добровольного страхования</li>
        </ul>
        <p>Минимальный размер добровольных взносов в 2023 году составляет 45 842 рубля за полный год.</p>

        <h3>Как вести учет доходов и расходов</h3>
        <p>
          Хотя самозанятые не обязаны вести учет расходов для целей налогообложения, рекомендуется сохранять информацию о доходах и расходах для собственного контроля бизнеса:
        </p>
        <ul>
          <li>Сохраняйте все чеки и квитанции о расходах — это пригодится при возможном переходе на УСН Доходы-Расходы</li>
          <li>Ведите учет всех поступлений в отдельной таблице или специальном приложении</li>
          <li>Отслеживайте сроки уплаты налога (до 25 числа следующего месяца)</li>
          <li>Контролируйте лимит дохода в 2,4 млн рублей, чтобы не потерять право на применение НПД</li>
        </ul>

        <h3>Переход с самозанятости на другие режимы</h3>
        <p>
          Если доходы превысят лимит или вы решите расширить бизнес, то потребуется переход на другой налоговый режим:
        </p>
        <ol>
          <li>Подайте заявление о снятии с учета в качестве самозанятого в приложении «Мой налог»</li>
          <li>Зарегистрируйтесь как ИП (если еще не являетесь им)</li>
          <li>Выберите и подайте заявление на применение подходящего налогового режима (УСН, ПСН, ЕСХН)</li>
          <li>Учтите особенности выбранного режима: необходимость отчетности, уплаты страховых взносов, ведения учета</li>
        </ol>

        <p className="font-semibold mt-6">
          Наш калькулятор налогов для самозанятых поможет вам оценить налоговую нагрузку, сравнить разные режимы налогообложения и спланировать свои финансы!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;